import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "components/buttons/Button";
import EmptyState from "components/EmptyState/EmptyState";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import { Modal } from "components/Modal/Modal";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { Checkbox } from "components/table/components/Checkbox/Checkbox";
import { LoyaltyRewardSelectorItem } from "services/loyaltyRewardsService";

import styles from "./LoyaltyProgramLevelsRewardsTab.module.scss";

interface Props {
  type: "BONUS" | "CASHBACK";
  items: LoyaltyRewardSelectorItem[];
  isLoading: boolean;
  hasError: boolean;
  selectedReferences: string[];
  onClose: () => void;
  onSave: (references: string[]) => void;
  onRetry: () => void;
}

const LoyaltyRewardEntitySelectorModal: React.FC<Props> = ({
  type,
  items,
  isLoading,
  hasError,
  selectedReferences,
  onClose,
  onSave,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [draftReferences, setDraftReferences] = useState(selectedReferences);

  useEffect(() => {
    setDraftReferences(selectedReferences);
  }, [selectedReferences, type]);

  const displayItems = useMemo<LoyaltyRewardSelectorItem[]>(() => {
    const available = new Set(items.map((item) => String(item.id)));
    const missing = selectedReferences
      .filter((reference) => !available.has(reference))
      .map((reference) => ({
        id: Number(reference),
        title: `${t(type.toLowerCase())} #${reference}`,
        status: t("unavailableRewardTarget"),
      }));
    return [...missing, ...items];
  }, [items, selectedReferences, t, type]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return displayItems;

    return displayItems.filter(
      (item) =>
        item.title.toLowerCase().includes(normalizedSearch) ||
        String(item.id).includes(normalizedSearch),
    );
  }, [displayItems, search]);

  const handleSave = () => {
    onSave(draftReferences);
  };

  const toggleReference = (reference: string) => {
    if (type === "CASHBACK") {
      setDraftReferences([reference]);
      return;
    }
    setDraftReferences((current) =>
      current.includes(reference)
        ? current.filter((item) => item !== reference)
        : [...current, reference],
    );
  };

  return (
    <div
      className={styles.nestedModalBoundary}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Modal onClose={onClose} closeOnOverlayClick={false}>
        <Modal.Header>
          {t(type === "BONUS" ? "selectLoyaltyBonus" : "selectActiveCashback")}
        </Modal.Header>
        <Modal.Content className={styles.selectorModalContent}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t("searchYourEntity")}
            isSmall
          />

          {isLoading ? (
            <ModalTabSkeleton />
          ) : hasError ? (
            <div className={styles.selectorError}>
              <EmptyState message={t("rewardTargetsLoadError")} />
              <Button isPrimary txt={t("retry")} handleClick={onRetry} />
            </div>
          ) : filteredItems.length === 0 ? (
            <EmptyState message={t("noRewardTargetsFound")} />
          ) : (
            <div
              className={styles.selectorList}
              role={type === "BONUS" ? "group" : "radiogroup"}
            >
              {filteredItems.map((item) => {
                const reference = String(item.id);
                const isSelected = draftReferences.includes(reference);

                return (
                  <div
                    key={item.id}
                    className={`${styles.selectorItem} ${
                      isSelected ? styles.selectorItemSelected : ""
                    }`}
                    role={type === "BONUS" ? "checkbox" : "radio"}
                    aria-checked={isSelected}
                    tabIndex={0}
                    onClick={() => toggleReference(reference)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        toggleReference(reference);
                      }
                    }}
                  >
                    {type === "BONUS" ? (
                      <Checkbox
                        id={reference}
                        checked={isSelected}
                        onClick={() => toggleReference(reference)}
                      />
                    ) : (
                      <span className={styles.selectorRadio} />
                    )}
                    {item.image && (
                      <img
                        className={styles.selectorImage}
                        src={item.image}
                        alt=""
                      />
                    )}
                    <span className={styles.selectorIdentity}>
                      <span className={styles.selectorTitle}>{item.title}</span>
                      <span className={styles.selectorMeta}>
                        ID {item.id}
                        {item.type ? ` · ${item.type}` : ""}
                        {item.status ? ` · ${item.status}` : ""}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Modal.Content>
        <Modal.Footer>
          <Button isSecondary txt={t("cancel")} handleClick={onClose} />
          <Button
            isPrimary
            txt={selectedReferences.length ? t("save") : t("add")}
            handleClick={handleSave}
            disabled={
              isLoading ||
              hasError ||
              (draftReferences.length === 0 && selectedReferences.length === 0)
            }
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LoyaltyRewardEntitySelectorModal;
